<script setup>
// Owner 2026-07-02 — the per-turn AI ACTION TRACE (mk-agent content_attributes.ai_card.trace):
// a small round (i) beside any message carrying the trace; hovering shows the numbered step
// list (planner → tool → gate → decision → delivery). The text is SELECTABLE on purpose — staff
// copy-paste the trace to an AI and ask "why did it do step 3 instead of X".
// Teleport + fixed + measured coordinates (the MemoryPopover pattern) so the panel escapes the
// bubble's stacking/clipping context; a short leave-grace lets the cursor travel into the panel.
import { ref, computed } from 'vue';

defineProps({
  steps: { type: Array, required: true },
  // The needs-human reason (ai_card.reason). When present it renders atop the panel, so ONE (i)
  // carries both the "why held" and the step list (the old reason-only ⓘ hides when a trace exists).
  reason: { type: String, default: '' },
});

const PANEL_W = 416; // w-[26rem]
const PANEL_H = 300; // ≈ max-h-72 + padding

const iconEl = ref(null);
const open = ref(false);
const pos = ref({ top: 0, left: 0 });
let hideTimer = null;

function show() {
  clearTimeout(hideTimer);
  const r = iconEl.value?.getBoundingClientRect();
  if (r) {
    const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8));
    const below = r.bottom + 4;
    const top =
      below + PANEL_H > window.innerHeight
        ? Math.max(8, r.top - PANEL_H - 4)
        : below;
    pos.value = { top, left };
  }
  open.value = true;
}

function hide() {
  hideTimer = setTimeout(() => {
    open.value = false;
  }, 200);
}

const panelStyle = computed(() => ({
  top: `${pos.value.top}px`,
  left: `${pos.value.left}px`,
}));
</script>

<template>
  <span
    ref="iconEl"
    class="inline-flex size-4 items-center justify-center rounded-full border border-current/40 text-[9px] font-semibold leading-none opacity-60 hover:opacity-100 cursor-help select-none"
    data-test-id="ai-trace-info"
    @mouseenter="show"
    @mouseleave="hide"
    >i</span
  >
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed z-[1000] w-[26rem] max-w-[80vw] max-h-72 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-lg p-2.5 text-[11px] leading-relaxed select-text cursor-text"
      :style="panelStyle"
      data-test-id="ai-trace-panel"
      @mouseenter="show"
      @mouseleave="hide"
      @click.stop
    >
      <div class="font-semibold mb-1 opacity-70">AI action trace</div>
      <div v-if="reason" class="mb-1.5 text-n-ruby-11">
        {{ reason }}
      </div>
      <div
        v-for="(s, i) in steps"
        :key="i"
        class="font-mono whitespace-pre-wrap break-words"
      >
        {{ i + 1 }}. {{ s }}
      </div>
    </div>
  </Teleport>
</template>
